import type * as THREE from "three";
import type { PanelColor } from "../interfaces/PanelColor";
import { CoatedPanelMaterial } from "./CoatedPanelMaterial";

interface PanelMaterialProps {
  color: PanelColor;
  /** Panel size for texture scaling - ensures consistent texture scale regardless of cutouts */
  panelSize?: THREE.Vector2;
  /** Use the phenolic film surface (faint grain, press haze) */
  textured?: boolean;
  /** Base roughness (0-1) */
  roughness?: number;
  /** How strongly the grain shows through the film (only for textured) */
  grainStrength?: number;
  /** Texture resolution (power of 2 recommended, only for textured) */
  textureSize?: number;
}

/**
 * Panels are laminated plywood, honeycomb plastic or Poly-Board — all
 * dielectrics, so metalness stays 0. Metals have no diffuse reflection, so any
 * metalness here would trade the panel's colour for a mirror of the studio.
 * The "metalic" finishes are metal-look laminates: that is a gloss difference,
 * expressed through roughness.
 */
const PANEL_METALNESS = 0;
/**
 * Glossier than the phenolic film (0.3), which is what sets the two finishes
 * apart in the render now that both are flat: the metal-look laminate shows
 * the studio's softbox as a tighter, brighter highlight.
 */
const METALLIC_LAMINATE_ROUGHNESS = 0.22;

export const PanelMaterial = ({
  color,
  panelSize,
  textured = false,
  roughness = 0.3,
  grainStrength,
  textureSize,
}: PanelMaterialProps) => {
  const panelRoughness = color.metalic
    ? METALLIC_LAMINATE_ROUGHNESS
    : roughness;

  // Use the coated-panel grain if enabled
  if (textured) {
    return (
      <CoatedPanelMaterial
        color={color}
        panelSize={panelSize}
        roughness={panelRoughness}
        metalness={PANEL_METALNESS}
        grainStrength={grainStrength}
        textureSize={textureSize}
      />
    );
  }

  // Fallback to simple standard material
  return (
    <meshStandardMaterial
      color={color.color}
      roughness={panelRoughness}
      metalness={PANEL_METALNESS}
    />
  );
};
